import { useEffect, useState } from "react";
import { PAGINATION } from "@/lib/constants";
import { useSearchParams } from "react-router-dom";
import { updateParams } from "@/lib/utils";

const debounceTime = 500;

export function useSearchPagination() {
  const [params, setParams] = useSearchParams();

  // 1. Read initial values from the URL (if present)
  const initialSearch = params.get("search");
  const initialPage =
    parseInt(params.get("pageNo") || "") || PAGINATION.DEFAULT_PAGE_NUMBER;
  const initialRows =
    parseInt(params.get("rowsPerPage") || "") ||
    PAGINATION.DEFAULT_ROWS_PER_PAGE;

  const [search, setSearch] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [pageSelected, setPageSelected] = useState(initialPage);
  const [rowsPerPageValue, setRowsPerPageValue] = useState(initialRows);

  const reset = () => {
    setSearch(null);
    setDebouncedSearch(null);
    setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
    setRowsPerPageValue(PAGINATION.DEFAULT_ROWS_PER_PAGE);

    // 3. Clear all three
    updateParams({ search: null, pageNo: null, rowsPerPage: null }, setParams);
  };

  // debouncing search input
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (search !== null) {
        setDebouncedSearch(search);
        setPageSelected(1);
      }
    }, debounceTime);
    return () => clearTimeout(timeout);
  }, [search]);

  // Whenever page, search or rows‑per‑page change, push them into URL
  useEffect(() => {
    const newParams = {
      pageNo: pageSelected,
    };

    if (debouncedSearch !== null) {
      newParams.search = debouncedSearch;
    } else {
      newParams.search = null;
    }

    updateParams(newParams, setParams);
  }, [pageSelected, debouncedSearch]);

  return {
    debouncedSearch,
    search: search ?? debouncedSearch,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
    reset,
  };
}
